import { Fragment, useMemo } from 'react';
import { parseBlocks, type Span } from './markdown-lite';

function Spans({ spans }: { spans: Span[] }) {
  return (
    <>
      {spans.map((s, i) => (
        <Fragment key={i}>
          {s.bold ? (
            <strong className="font-semibold text-foreground">{s.text}</strong>
          ) : s.code ? (
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">{s.text}</code>
          ) : (
            s.text
          )}
        </Fragment>
      ))}
    </>
  );
}

/**
 * An assistant reply, with its structure shown rather than spelled out.
 *
 * Everything is built as React elements from parsed data — no HTML is ever
 * constructed from the model's output, so a reply cannot inject markup no matter
 * what it contains.
 */
export function RichText({ text, compact }: { text: string; compact?: boolean }) {
  const blocks = useMemo(() => {
    const all = parseBlocks(text);
    if (!compact) return all;
    // Keep the lead-in and drop the restated list: whatever follows is already
    // on screen as a chart. Never return nothing — a reply that is only a list
    // still needs its first line.
    const firstList = all.findIndex((b) => b.type === 'bullet' || b.type === 'numbered');
    return firstList <= 0 ? all.slice(0, 1) : all.slice(0, firstList);
  }, [text, compact]);

  return (
    <div className="space-y-1.5 text-xs leading-relaxed text-foreground">
      {blocks.map((block, i) => {
        if (block.type === 'heading') {
          return (
            <p
              key={i}
              className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <Spans spans={block.spans} />
            </p>
          );
        }
        if (block.type === 'bullet') {
          return (
            <div key={i} className="flex gap-2 pl-0.5">
              <span aria-hidden className="select-none text-muted-foreground">
                •
              </span>
              <span className="min-w-0 flex-1">
                <Spans spans={block.spans} />
              </span>
            </div>
          );
        }
        if (block.type === 'numbered') {
          return (
            <div key={i} className="flex gap-2 pl-0.5">
              <span className="select-none tabular-nums text-muted-foreground">{block.index}.</span>
              <span className="min-w-0 flex-1">
                <Spans spans={block.spans} />
              </span>
            </div>
          );
        }
        return (
          <p key={i}>
            <Spans spans={block.spans} />
          </p>
        );
      })}
    </div>
  );
}
